import { Dispatch, FC, SetStateAction, Suspense, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectedTeacherDashboardFilter,
  setTeacherDashboardFilter,
} from '../../redux/reducers/teacherDashboardFiltersSlice';
import { useLazyLoadQuery } from 'react-relay';
import { getTotalStudentsFiltered } from '../../graphQLOperations/Dashboard';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { Card } from 'primereact/card';
import { OverlayPanel } from 'primereact/overlaypanel';
import { InputSwitch } from 'primereact/inputswitch';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { DashboardGetStudentsCountQuery } from '../../graphQLOperations/__generated__/DashboardGetStudentsCountQuery.graphql';
import { selectUser } from '../../redux/reducers/userSlice';
import { ClassesAndRoadmapsDropdown } from './ClassesAndRoadmapsDropdown';
import { FilterOptionValues } from './TeacherDashboard';
import { RotateCcw } from 'lucide-react';
import { DashboardStudentsRosterQuery$data } from '../../graphQLOperations/__generated__/DashboardStudentsRosterQuery.graphql';

interface Props {
  filterOption: FilterOptionValues;
  setFilterOption: Dispatch<SetStateAction<FilterOptionValues>>;
  hasUserClassWithActiveStudent?: DashboardStudentsRosterQuery$data['hasUserClassWithActiveStudent'];
  noStudentsInSite: boolean;
  setNoStudentInSite: Dispatch<SetStateAction<boolean>>;
}

interface TotalStudentProps {
  setNoStudentInSite: Dispatch<SetStateAction<boolean>>;
  siteLength: number;
}

export const TeacherDashboardFilterAdmin: FC<Props> = ({
  filterOption,
  setFilterOption,
  hasUserClassWithActiveStudent,
  noStudentsInSite,
  setNoStudentInSite,
}) => {
  const dispatch = useAppDispatch();
  const userRedux = useAppSelector(selectUser);
  const teacherDashboardFilters = useAppSelector(selectedTeacherDashboardFilter);
  const [dropdownSitesValue, setDropdownSitesValue] = useState<{ id: string; name: string }[]>([]);
  const [dropdownRoadmapsValues, setDropdownRoadmapsValues] = useState<{ id: string; name: string }[]>([]);
  const [selectedOption, setSelectedOption] = useState<{ id: string; name: string }[]>([]);
  const [siteIndex, setSiteIndex] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const [multiselectEnabled, setMultiselectEnabled] = useState(false);
  const op = useRef<OverlayPanel>(null);
  const [orderedClasses, setOrdererClasses] = useState<{ id: string; name: string }[]>([]);
  const sites = [...(userRedux?.sites || [])];
  sites.sort((a, b) => (a.name > b.name ? 1 : -1));

  useEffect(() => {
    dispatch(
      setTeacherDashboardFilter({
        ...teacherDashboardFilters,
        sites: [
          {
            id: sites[siteIndex].id || '',
            name: sites[siteIndex].name,
          },
        ],
      }),
    );
    setDropdownSitesValue([{ id: sites[siteIndex].id || '', name: sites[siteIndex].name }]);
  }, [siteIndex, resetKey]);

  const getRoadmaps = () => {
    const roadmaps: { id: string; name: string }[] = [];
    userRedux?.sites?.forEach(element => {
      if (teacherDashboardFilters.sites?.map(item => item.id).includes(element.id)) {
        element.targetGroups?.forEach(tg => {
          if (roadmaps.every(rm => rm.id !== tg.id)) {
            roadmaps.push({ id: tg.id, name: tg.name });
          }
        });
      }
    });
    return roadmaps;
  };

  useEffect(() => {
    if (teacherDashboardFilters.sites?.length) {
      if (filterOption === FilterOptionValues.ROADMAP) {
        const roadmaps = getRoadmaps();
        setDropdownRoadmapsValues(roadmaps);
        if (roadmaps.length) {
          dispatch(
            setTeacherDashboardFilter({
              ...teacherDashboardFilters,
              roadmaps: [{ id: roadmaps[0].id, name: roadmaps[0].name }],
            }),
          );
          setSelectedOption([{ id: roadmaps[0].id, name: roadmaps[0].name }]);
        }
      }
    }
  }, [teacherDashboardFilters.sites, filterOption]);

  const resetSubInput = () => {
    if (filterOption === FilterOptionValues.CLASSES) {
      setFilterOption(FilterOptionValues.ROADMAP);
    } else {
      const roadmaps = getRoadmaps();
      setDropdownRoadmapsValues(roadmaps);
      if (roadmaps.length) {
        dispatch(
          setTeacherDashboardFilter({
            ...teacherDashboardFilters,
            roadmaps: [{ id: roadmaps[0].id, name: roadmaps[0].name }],
          }),
        );
        setSelectedOption([{ id: roadmaps[0].id, name: roadmaps[0].name }]);
      }
    }
  };

  const onPageChange = (e: PaginatorPageChangeEvent) => {
    setSiteIndex(e.first);
  };

  const handleSiteSelection = (id: string, pill?: boolean) => {
    const selectedSite = sites.find(st => st.id === id);
    const prevSites = dropdownSitesValue || [];

    if (prevSites.length === 1 && selectedSite?.id === prevSites[0].id) {
      return;
    }

    if (prevSites?.some(st => st.id === id)) {
      if (pill) {
        dispatch(
          setTeacherDashboardFilter({ ...teacherDashboardFilters, sites: prevSites.filter(st => st.id !== id) }),
        );
      }

      setDropdownSitesValue(dropdownSitesValue.filter(st => st.id !== id));
    } else {
      setDropdownSitesValue([...prevSites, { id: selectedSite?.id || '', name: selectedSite?.name || '' }]);
    }
  };

  const handleMultiselect = (state: boolean) => {
    if (!state) {
      setSiteIndex(0);
      setResetKey(new Date().getTime());
    }
    setMultiselectEnabled(state);
  };

  const applyFilters = (mode: 'site' | 'roadmap-class') => {
    if (mode === 'roadmap-class') {
      if (filterOption === FilterOptionValues.ROADMAP) {
        dispatch(setTeacherDashboardFilter({ ...teacherDashboardFilters, roadmaps: selectedOption }));
      } else {
        dispatch(setTeacherDashboardFilter({ ...teacherDashboardFilters, classes: selectedOption }));
      }
    } else {
      dispatch(setTeacherDashboardFilter({ ...teacherDashboardFilters, sites: dropdownSitesValue }));
    }
  };

  const renderSubReset =
    (teacherDashboardFilters.classes && teacherDashboardFilters.classes.length > 1) ||
    (teacherDashboardFilters.roadmaps && teacherDashboardFilters.roadmaps.length > 1);

  return (
    <Card
      className="nc_block p-0 mb-2"
      pt={{
        content: {
          style: { alignItems: 'stretch' },
        },
      }}
    >
      <div className="flex align-items-start h-4rem w-full pt-0 mt-0 tw-justify-start">
        <div className="tw-flex tw-items-center tw-w-full tw-h-16">
          <div className="tw-flex tw-items-center tw-gap-3">
            <Paginator
              className="mt-2 pt-0 natural-900"
              first={siteIndex}
              rows={1}
              totalRecords={sites.length || 0}
              onPageChange={e => {
                if (multiselectEnabled) return;
                onPageChange(e);
              }}
              template={{
                layout: `PrevPageLink ${!multiselectEnabled ? 'CurrentPageReport' : ''} NextPageLink`,
                CurrentPageReport: options => (
                  <span className="px-2 text-sm">
                    (<strong className="font-bold">{options.first}</strong>/{sites.length})
                  </span>
                ),
              }}
              prevPageLinkIcon={<i className="nc_icon-nc_left icon-sm mr-2 cursor-pointer"></i>}
              nextPageLinkIcon={<i className="nc_icon-nc_right icon-sm ml-2 cursor-pointer"></i>}
            />

            <span
              onKeyDown={e => op.current?.toggle(e)}
              tabIndex={0}
              onClick={e => op.current?.toggle(e)}
              style={{ fontFamily: 'Poppins' }}
              className="font-bold text-xl text-primary underline cursor-pointer"
            >
              {teacherDashboardFilters.sites
                ? multiselectEnabled
                  ? 'Multiple sites selected'
                  : teacherDashboardFilters.sites[0].name
                : ''}
            </span>
          </div>
        </div>

        <OverlayPanel ref={op} style={{ fontFamily: 'Poppins' }}>
          <div className="flex flex-column">
            <div className="flex aling-items-center border-bottom-1 pb-2 mb-1 surface-border w-full">
              <span className="text-base font-semibold pt-1 mr-2">Select multiple sites</span>
              <InputSwitch
                className="col-3 max-w-3rem"
                checked={multiselectEnabled}
                onChange={e => {
                  handleMultiselect(e.target.value);
                }}
              />
            </div>
            {sites.map((cl, inx) => (
              <div key={cl.id} className="flex align-items-center">
                {multiselectEnabled && (
                  <Checkbox
                    onClick={() => handleSiteSelection(cl.id)}
                    checked={dropdownSitesValue.some(st => st.id === cl.id) || false}
                  />
                )}
                <span
                  onClick={() => {
                    if (multiselectEnabled) {
                      return;
                    }
                    setSiteIndex(inx);
                  }}
                  className={`font-semibold block w-full my-2 ${
                    inx !== siteIndex || multiselectEnabled ? 'text-primary cursor-pointer' : ''
                  }`}
                >
                  {cl.name}
                </span>
              </div>
            ))}
            {multiselectEnabled && (
              <Button className="mt-2" label="Apply filter" outlined onClick={() => applyFilters('site')} />
            )}
          </div>
        </OverlayPanel>
      </div>

      {hasUserClassWithActiveStudent && teacherDashboardFilters.sites && (
        <Suspense>
          <TotalStudentsOnlySite
            setNoStudentInSite={setNoStudentInSite}
            siteLength={teacherDashboardFilters.sites.length}
          />
        </Suspense>
      )}

      {multiselectEnabled && (
        <div className="flex tw-items-center w-full px-6 grid gap-2">
          <div className="tw-w-[90%]">
            {teacherDashboardFilters.sites?.map(cl => (
              <Button
                key={cl.id}
                className="mx-1 mt-1 h-2rem font-normal"
                icon="pi pi-times-circle"
                style={{ fontFamily: 'Poppins' }}
                iconPos="right"
                outlined
                label={cl.name}
                onClick={() => handleSiteSelection(cl.id, true)}
              ></Button>
            ))}
          </div>

          {multiselectEnabled && (
            <Button onClick={() => handleMultiselect(false)} outlined className="tw-px-3 tw-pt-1 tw-min-w-[40px]">
              <RotateCcw />
            </Button>
          )}
        </div>
      )}

      <div className="w-11 px-4 pb-3">
        <hr className="my-0 py-0" />
        <div className="flex align-items-center mt-2 w-full">
          <div className="tw-w-[95%] tw-flex tw-items-center">
            <div className="w-2">
              <span
                className={`tw-font-atkinson tw-font-bold ${
                  !hasUserClassWithActiveStudent || noStudentsInSite ? 'tw-text-neutral-600' : ''
                }`}
              >
                View by
              </span>
            </div>
            <div className="flex align-items-center w-full">
              <div
                className={`ml-3 p-1 border-1 ${
                  !hasUserClassWithActiveStudent || noStudentsInSite ? 'tw-border-neutral-600' : ' border-primary'
                } border-round-md`}
              >
                <Button
                  onClick={() => {
                    if (filterOption === FilterOptionValues.ROADMAP) {
                      setFilterOption(FilterOptionValues.CLASSES);
                    } else {
                      setFilterOption(FilterOptionValues.ROADMAP);
                    }
                  }}
                  className={`${
                    filterOption === FilterOptionValues.ROADMAP
                      ? !hasUserClassWithActiveStudent || noStudentsInSite
                        ? 'text-color border-none bg-white'
                        : 'nc_rectangle-button'
                      : 'text-color border-none bg-white'
                  }`}
                  label="Roadmap"
                  disabled={!hasUserClassWithActiveStudent || noStudentsInSite}
                />
                <Button
                  onClick={() => {
                    if (filterOption === FilterOptionValues.CLASSES) {
                      setFilterOption(FilterOptionValues.ROADMAP);
                    } else {
                      setFilterOption(FilterOptionValues.CLASSES);
                    }
                  }}
                  className={`${
                    filterOption === FilterOptionValues.CLASSES
                      ? !hasUserClassWithActiveStudent || noStudentsInSite
                        ? 'text-color border-none bg-white'
                        : 'nc_rectangle-button'
                      : 'text-color border-none bg-white'
                  }`}
                  label="Classes"
                  disabled={!hasUserClassWithActiveStudent || noStudentsInSite}
                />
              </div>
              {!hasUserClassWithActiveStudent ||
                (noStudentsInSite && (
                  <h1 className="tw-font-atkinson tw-text-neutral-600 tw-text-lg tw-font-bold tw-pl-4">
                    No classes yet
                  </h1>
                ))}
              {hasUserClassWithActiveStudent && !noStudentsInSite && (
                <>
                  <div className="mx-3" style={{ width: '1px', height: '3rem', background: 'grey' }}></div>
                  <div className="w-3">
                    <Suspense>
                      <ClassesAndRoadmapsDropdown
                        setOrderedClasses={setOrdererClasses}
                        filterOption={filterOption}
                        setSelectedOption={setSelectedOption}
                        availableOptions={
                          filterOption === FilterOptionValues.ROADMAP
                            ? dropdownRoadmapsValues
                            : orderedClasses.map(cl => ({ id: cl.id, name: cl.name || '' }))
                        }
                        selectedOptions={selectedOption}
                        applyFilters={() => applyFilters('roadmap-class')}
                      />
                    </Suspense>
                  </div>
                  <div>
                    <span className="ml-2 neutral-900">
                      {' '}
                      {'( '}{' '}
                      <Suspense>
                        <TotalStudentsWithFilters mode={filterOption} />
                      </Suspense>
                      {' students)'}
                    </span>
                  </div>
                </>
              )}
            </div>

            {renderSubReset && (
              <Button onClick={resetSubInput} outlined className="tw-px-3 tw-pt-1">
                <RotateCcw />
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

const TotalStudentsWithFilters: FC<{ mode: FilterOptionValues }> = ({ mode }) => {
  const teacherDashboardFilters = useAppSelector(selectedTeacherDashboardFilter);
  const { allStudents } = useLazyLoadQuery<DashboardGetStudentsCountQuery>(
    getTotalStudentsFiltered,
    {
      filter: {
        siteIds: teacherDashboardFilters.sites?.length ? teacherDashboardFilters.sites.map(st => st.id) : null,
        targetGroupIds:
          mode === FilterOptionValues.ROADMAP && teacherDashboardFilters.roadmaps
            ? teacherDashboardFilters.roadmaps.map(rm => rm.id)
            : null,
        classIds: mode === FilterOptionValues.CLASSES ? teacherDashboardFilters.classes?.map(cl => cl.id) : null,
        gradeLevelIds: teacherDashboardFilters.grades?.length ? teacherDashboardFilters.grades?.map(gd => gd.id) : null,
      },
    },
    { fetchPolicy: 'network-only' },
  );
  return <span className="natural-900">{allStudents?.totalCount}</span>;
};

const TotalStudentsOnlySite: FC<TotalStudentProps> = ({ setNoStudentInSite, siteLength }) => {
  const teacherDashboardFilters = useAppSelector(selectedTeacherDashboardFilter);
  const { allStudents } = useLazyLoadQuery<DashboardGetStudentsCountQuery>(
    getTotalStudentsFiltered,
    {
      filter: {
        siteIds: teacherDashboardFilters.sites?.length ? teacherDashboardFilters.sites.map(st => st.id) : null,
        gradeLevelIds: teacherDashboardFilters.grades?.length ? teacherDashboardFilters.grades?.map(gd => gd.id) : null,
      },
    },
    { fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    allStudents?.totalCount === 0 ? setNoStudentInSite(true) : setNoStudentInSite(false);
  }, [allStudents]);

  return (
    <>
      {allStudents?.totalCount && allStudents?.totalCount > 0 ? (
        <div className="tw-block pl-3 w-full tw-font-sans tw-text-sm">
          <span className="ml-2 neutral-900">
            {siteLength === 1 ? (
              <span className="natural-900">
                {'('} {allStudents?.totalCount} {' students)'}
              </span>
            ) : (
              <>
                <span className="font-semibold">{`${siteLength} sites selected - `}</span>
                <span className="natural-900">
                  {allStudents?.totalCount} {' students'}
                </span>
              </>
            )}
          </span>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
